import { useCallback, useContext } from 'react';
import { submitDigitsEditorValue } from '@decisions/server/DecisionsFramework/Design/Properties/PropertyEditorService';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { IEditorProps } from './Editor';
import { getNumericEditorType, isNullableType, isFloatEditor } from './utils/InputTypeHelpers';
import { EditorTextField } from './EditorTextField';
import { parseInputToNumber, initializeNumericValue } from 'utils/NumericFormattingUtils';
import { childValueIsNull } from 'components/property-editors/utils/ProviderNodeUtils';
import { AwaitableContext } from 'contexts/AwaitableContext';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';

export interface IDigitsEditorProps extends Omit<IEditorProps, 'onChange'> {}

/**
 * A Digits Editor
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function DigitsEditor(props: IDigitsEditorProps) {
  const { child } = props;
  const { editingContextId, refreshContext } = useContext(PropertyEditorContext);
  const { addPromise, removePromise } = useContext(AwaitableContext);
  const float = isFloatEditor(child);
  const nullable = isNullableType(child);
  const placeholder = getEditorConfigValue(child, 'placeholder');
  // using useCallback for debounce within EditorTextField, not for performance.
  const formatValue = useCallback((value: string) => parseInputToNumber(value, float, nullable), [float, nullable]);

  const initializer = (value: string | null) =>
    !childValueIsNull(child) ? initializeNumericValue(value, nullable) : '';

  const onChange = useCallback(
    async (value: any, path: string[]) => {
      return new Promise<void>(resolve => {
        const id = path.join(',');
        addPromise(
          id,
          (async () => {
            // instead of setValue, call editor service method which cleans inputs for digits editors
            await submitDigitsEditorValue({
              editingContext: editingContextId,
              path,
              value,
              type: getNumericEditorType(child),
              isnullableType: isNullableType(child),
            });
            removePromise(id);
            refreshContext(); // TODO too late to change/test now, but digits editor probably shouldn't be triggering a full refresh
            resolve();
          })()
        );
      });
    },
    [addPromise, child, editingContextId, refreshContext, removePromise]
  );

  return (
    <EditorTextField
      {...props}
      onChange={onChange}
      formatHelper={formatValue}
      initialValueHelper={initializer}
      placeholder={placeholder}
    />
  );
}
